/**
 * TaskTemplates - One-click task templates for quick hiring
 * Features: Pre-built forms, free first 5 tasks, instant results
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ShoppingBag, FileText, Share2, Mail, Code, 
  Loader2, CheckCircle, ArrowLeft, Sparkles, Gift,
  Zap, ArrowRight, Layout, Wrench, Smartphone, Monitor,
  Workflow, Search, Rocket, Wand2, Brain, BarChart3,
  Image, Video, Palette, Package, Download, Info, AlertTriangle,
  User, DollarSign, MessageSquare, Briefcase, PenLine, Calculator,
  Volume2, Heart, Shuffle, Flame, Twitter, Linkedin, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import TaskFeedback from '@/components/TaskFeedback';
import TommyLoadingAnimation from '@/components/TommyLoadingAnimation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  ShoppingBag,
  FileText,
  Share2,
  Mail,
  Code,
  Layout,
  Wrench,
  Smartphone,
  Workflow: Zap,
  Search,
  Rocket,
  Wand: Wand2,
  Brain,
  BarChart: BarChart3,
  Image,
  Video,
  Palette,
  Package,
  User,
  DollarSign,
  MessageSquare,
  Briefcase,
  PenLine,
  Calculator
};

export default function TaskTemplates() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [hasProfile, setHasProfile] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showTommyLoader, setShowTommyLoader] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [freeTasksRemaining, setFreeTasksRemaining] = useState(5);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(true);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);
  // Voiceover state
  const [addVoiceover, setAddVoiceover] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [voices, setVoices] = useState([]);
  // Phase 1 Quick Wins state
  const [favorites, setFavorites] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [surpriseTemplate, setSurpriseTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchVoices();
    if (user && token) {
      fetchFreeTaskStatus();
      fetchRecommendations();
      fetchFavorites();
      fetchStreak();
    }
  }, [user, token]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data.favorites || []);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  };

  const fetchStreak = async () => {
    try {
      const response = await axios.get(`${API}/user/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreak(response.data);
    } catch (err) {
      console.error('Failed to fetch streak:', err);
    }
  };

  const toggleFavorite = async (templateId) => {
    if (!user || !token) return;
    
    const isFavorited = favorites.includes(templateId);
    try {
      if (isFavorited) {
        await axios.post(`${API}/user/favorites/remove`, { template_id: templateId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(favorites.filter(id => id !== templateId));
      } else {
        await axios.post(`${API}/user/favorites/add`, { template_id: templateId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites([...favorites, templateId]);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const handleSurpriseMe = async () => {
    try {
      const response = await axios.get(`${API}/user/surprise-template`);
      const { template, message } = response.data;
      setSurpriseTemplate({ ...template, message });
      // Find and select the template
      const found = templates.find(t => t.id === template.id);
      if (found) {
        setSelectedTemplate(found);
      }
    } catch (err) {
      console.error('Surprise me failed:', err);
    }
  };

  const shareToSocial = async (platform, contentType = 'task', contentId = currentTaskId) => {
    try {
      const response = await axios.post(`${API}/user/share/generate`, {
        content_type: contentType,
        content_id: contentId || 'general',
        platform
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.share_link, '_blank');
    } catch (err) {
      console.error('Share failed:', err);
      // Fallback share
      const text = encodeURIComponent("Check out TripleTC - AI Agent Marketplace!");
      const url = encodeURIComponent("https://tripletc.com");
      if (platform === 'twitter') {
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
      } else {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
      }
    }
  };

  const fetchVoices = async () => {
    try {
      const response = await axios.get(`${API}/video/voices`);
      setVoices(response.data.voices || []);
    } catch (err) {
      console.error('Failed to fetch voices:', err);
      // Fallback voices
      setVoices([
        { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
        { id: 'nova', name: 'Nova', description: 'Energetic, upbeat' },
        { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative' },
        { id: 'shimmer', name: 'Shimmer', description: 'Bright, cheerful' }
      ]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/templates/recommended`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
      setHasProfile(response.data.has_profile || false);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
    }
  };

  const fetchFreeTaskStatus = async () => {
    try {
      const response = await axios.get(`${API}/templates/free-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreeTasksRemaining(response.data.free_tasks_remaining);
    } catch (err) {
      console.error('Failed to fetch free task status:', err);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFieldValues({});
    setResult(null);
    setError('');
  };

  const handleFieldChange = (fieldName, value) => {
    console.log('Field changed:', fieldName, value);
    setFieldValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/auth?redirect=/templates');
      return;
    }

    // Validate required fields
    for (const field of selectedTemplate.fields) {
      if (field.required && !fieldValues[field.name]) {
        setError(`Please fill in: ${field.label}`);
        return;
      }
    }

    setSubmitting(true);
    setError('');

    // Check if this is a video task - use async polling
    const isVideoTask = selectedTemplate.is_media && selectedTemplate.media_type === 'video';
    const isMediaTask = selectedTemplate.is_media;
    
    if (isVideoTask) {
      // Use new async video job API
      await handleVideoSubmit();
      return;
    }
    
    if (isMediaTask) {
      setShowTommyLoader(true);
      setLoadingMessage('Creating your image...');
    }

    try {
      const response = await axios.post(`${API}/templates/submit`, {
        template_id: selectedTemplate.id,
        field_values: fieldValues,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000  // 2 minutes for image generation
      });

      setShowTommyLoader(false);
      setError('');

      if (response.data.requires_payment) {
        // User hit free limit - need payment
        setError(response.data.error || 'Free limit reached. Payment required for additional generations.');
        return;
      }

      if (response.data.is_free_task) {
        // Free task - show result immediately
        setFreeTasksRemaining(response.data.free_tasks_remaining);
        setCurrentTaskId(response.data.work_order_id);
        if (response.data.status === 'completed') {
          setResult(response.data.result);
          setShowFeedback(true);
        } else {
          setError(response.data.error || 'Task failed');
        }
      } else if (response.data.checkout_url) {
        // Need payment - redirect to checkout
        window.location.href = response.data.checkout_url;
      } else {
        setError(response.data.error || 'Something went wrong');
      }
    } catch (err) {
      setShowTommyLoader(false);
      console.error('Submit error:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit task. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Async video submission with polling
  const handleVideoSubmit = async () => {
    setShowTommyLoader(true);
    setLoadingMessage('Starting video generation... Tommy is warming up!');
    setVideoProgress(0);

    try {
      // Build prompt from field values
      const promptField = selectedTemplate.fields.find(f => f.name === 'prompt' || f.name === 'description' || f.name === 'video_description');
      const prompt = fieldValues[promptField?.name] || fieldValues.prompt || fieldValues.description || Object.values(fieldValues).join(' ');
      
      // Get duration (limited to 4, 8, or 12 seconds)
      const durationStr = fieldValues.duration || '4 seconds';
      const durationNum = parseInt(durationStr.split(' ')[0]) || 4;
      const duration = durationNum <= 4 ? 4 : durationNum <= 8 ? 8 : 12;

      // Start video job
      const startResponse = await axios.post(`${API}/video/start`, {
        prompt: prompt,
        duration: duration,
        template_id: selectedTemplate.id,
        add_voiceover: addVoiceover,
        voice: selectedVoice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const jobId = startResponse.data.job_id;
      setVideoJobId(jobId);
      setCurrentTaskId(jobId);
      setLoadingMessage('Generating your video... Tommy is on it!');

      // Poll for completion
      const pollInterval = 5000; // 5 seconds
      const maxPolls = 150; // 12.5 min for standard
      let pollCount = 0;

      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(`${API}/video/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const { status, progress, video_data, video_url, thumbnail, error: jobError, current_segment, total_segments, provider } = statusResponse.data;
          
          setVideoProgress(progress || 0);
          
          if (progress > 0 && progress < 100) {
            if (current_segment && total_segments) {
              setLoadingMessage(`Generating segment ${current_segment}/${total_segments}... ${progress}% complete`);
            } else {
              setLoadingMessage(`Generating video... ${progress}% complete`);
            }
          }

          if (status === 'completed') {
            setShowTommyLoader(false);
            setSubmitting(false);
            setResult({
              video_base64: video_data,
              video_url: video_url,  // fal.ai returns URL for large videos
              thumbnail: thumbnail,
              content_type: 'video/mp4',
              duration: duration,
              provider: provider || 'fal.ai'
            });
            setShowFeedback(true);
            return;
          }

          if (status === 'failed') {
            setShowTommyLoader(false);
            setSubmitting(false);
            
            // Check if user was credited for the failure
            const credited = statusResponse.data.credited;
            let errorMsg = jobError || 'Video generation failed. Please try again.';
            
            if (credited) {
              errorMsg += '\n\n✅ You\'ve been credited a free retry. Try again with a different prompt!';
            }
            
            setError(errorMsg);
            return;
          }

          // Continue polling
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(pollStatus, pollInterval);
          } else {
            setShowTommyLoader(false);
            setSubmitting(false);
            setError('Video generation is taking longer than expected. Check your dashboard in a few minutes.');
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
          // Don't fail on single poll error, keep trying
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(pollStatus, pollInterval);
          } else {
            setShowTommyLoader(false);
            setSubmitting(false);
            setError('Lost connection while generating. Check your dashboard for the result.');
          }
        }
      };

      // Start polling
      setTimeout(pollStatus, pollInterval);

    } catch (err) {
      setShowTommyLoader(false);
      setSubmitting(false);
      console.error('Video submit error:', err);
      
      const errorDetail = err.response?.data?.detail;
      
      if (err.response?.status === 402) {
        setError(errorDetail || 'Free video limit reached. Additional videos require payment.');
      } else if (err.response?.status === 400 && errorDetail?.error === 'content_policy_violation') {
        // Content policy error - show helpful message
        setError(`⚠️ ${errorDetail.message}\n\n💡 ${errorDetail.suggestion}`);
      } else {
        setError(errorDetail?.message || errorDetail || 'Failed to start video generation. Please try again.');
      }
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      value: fieldValues[field.name] || '',
      onChange: (e) => handleFieldChange(field.name, e.target.value),
      className: "bg-zinc-800 border-zinc-700 text-white",
      placeholder: field.placeholder
    };

    if (field.type === 'textarea') {
      return <Textarea {...commonProps} className={`${commonProps.className} min-h-[120px]`} />;
    }

    if (field.type === 'select') {
      // Video duration is limited to 4-12 seconds (Kling API limitation)
      const isVideoDuration = field.name === 'duration' && selectedTemplate?.media_type === 'video';
      
      // Only show valid duration options for video
      let options = field.options;
      if (isVideoDuration) {
        options = ["4 seconds", "8 seconds", "12 seconds"];
      }
      
      return (
        <select
          id={field.name}
          value={fieldValues[field.name] || ''}
          onChange={(e) => {
            console.log('Select changed:', field.name, e.target.value);
            handleFieldChange(field.name, e.target.value);
          }}
          className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white"
        >
          <option value="">Select...</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    return <Input {...commonProps} />;
  };

  // Show Tommy loading animation for media tasks
  if (showTommyLoader) {
    return <TommyLoadingAnimation message={loadingMessage} progress={videoProgress > 0 ? videoProgress : null} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  // Show result
  if (result) {
    const isAppTemplate = selectedTemplate?.category === 'apps';
    const isMediaResult = result.image_base64 || result.video_base64 || result.video_url;
    
    // Handle media results
    if (isMediaResult) {
      return (
        <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-zinc-900/50 border border-green-500/30 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {(result.video_base64 || result.video_url) ? 'Video' : 'Image'} Generated!
                  </h2>
                  <p className="text-zinc-400">Your {selectedTemplate?.name} is ready</p>
                </div>
              </div>

              {/* Image Preview */}
              {result.image_base64 && (
                <div className="mb-6">
                  <div className="bg-zinc-800 rounded-xl overflow-hidden border border-zinc-700">
                    <img
                      src={`data:image/png;base64,${result.image_base64}`}
                      alt="Generated image"
                      className="w-full h-auto max-h-[500px] object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Video Preview */}
              {(result.video_base64 || result.video_url) && (
                <div className="mb-6">
                  <div className="bg-zinc-800 rounded-xl overflow-hidden border border-zinc-700">
                    <video
                      controls
                      autoPlay
                      playsInline
                      loop
                      className="w-full h-auto max-h-[500px]"
                      src={result.video_url || `data:video/mp4;base64,${result.video_base64}`}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <p className="text-zinc-500 text-sm mt-2">Duration: {result.duration || 4} seconds • Provider: {result.provider || 'fal.ai'}</p>
                </div>
              )}

              {/* Feedback Component */}
              {showFeedback && currentTaskId && token && (
                <div className="mb-6">
                  <TaskFeedback 
                    taskId={currentTaskId}
                    templateId={selectedTemplate?.id}
                    token={token}
                    onClose={() => setShowFeedback(false)}
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => {
                    setSelectedTemplate(null);
                    setResult(null);
                    setFieldValues({});
                    setCurrentTaskId(null);
                    setShowFeedback(true);
                  }}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Templates
                </Button>
                <Button
                  onClick={() => {
                    // Download image/video
                    if (result.video_url) {
                      // Open external URL in new tab
                      window.open(result.video_url, '_blank');
                    } else {
                      const link = document.createElement('a');
                      if (result.image_base64) {
                        link.href = `data:image/png;base64,${result.image_base64}`;
                        link.download = `${selectedTemplate?.id || 'generated'}-image.png`;
                      } else if (result.video_base64) {
                        link.href = `data:video/mp4;base64,${result.video_base64}`;
                        link.download = `${selectedTemplate?.id || 'generated'}-video.mp4`;
                      }
                      link.click();
                    }
                  }}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {result.video_url ? 'Open Video' : `Download ${result.video_base64 ? 'Video' : 'Image'}`}
                </Button>
                <Button
                  onClick={() => navigate('/user-dashboard')}
                  variant="outline"
                  className="border-teal-500/50 text-teal-400"
                >
                  View My Dashboard
                </Button>
                {(result.video_base64 || result.video_url) && (
                  <Button
                    onClick={() => navigate('/video-gallery')}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Video Gallery
                  </Button>
                )}
                
                {/* Share Buttons */}
                <div className="flex gap-2 ml-auto">
                  <Button
                    onClick={() => shareToSocial('twitter', result.video_url ? 'video' : 'image', currentTaskId)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-400 hover:text-blue-400 hover:border-blue-400/50"
                  >
                    <Twitter className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => shareToSocial('linkedin', result.video_url ? 'video' : 'image', currentTaskId)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-400 hover:text-blue-600 hover:border-blue-600/50"
                  >
                    <Linkedin className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      );
    }
    
    // Regular text/code result
    const resultText = typeof result === 'object' 
      ? (result.result || result.output || JSON.stringify(result, null, 2))
      : result;
    
    // Extract HTML code from the result if it exists
    const htmlMatch = resultText.match(/```html\n([\s\S]*?)```/) || resultText.match(/<(!DOCTYPE|html)/i);
    const htmlCode = htmlMatch ? (htmlMatch[1] || resultText) : null;
    
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
        <div className={`mx-auto px-6 ${isAppTemplate ? 'max-w-6xl' : 'max-w-3xl'}`}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900/50 border border-green-500/30 rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Task Completed!</h2>
                <p className="text-zinc-400">Your {selectedTemplate?.name} is ready</p>
              </div>
            </div>

            {/* App preview for app building templates */}
            {isAppTemplate && htmlCode && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">Live Preview</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const blob = new Blob([htmlCode], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                    }}
                    className="border-zinc-700 text-zinc-300"
                  >
                    Open in New Tab
                  </Button>
                </div>
                <div className="bg-white rounded-xl overflow-hidden border border-zinc-700">
                  <iframe
                    srcDoc={htmlCode}
                    className="w-full h-[500px]"
                    title="App Preview"
                    sandbox="allow-scripts"
                  />
                </div>
              </div>
            )}

            {/* Code/Text output */}
            <div className="bg-zinc-800/50 rounded-xl p-6 mb-6 max-h-[400px] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-zinc-200 text-sm leading-relaxed font-mono">
                {resultText}
              </pre>
            </div>

            {/* Feedback Component */}
            {showFeedback && currentTaskId && token && (
              <div className="mb-6">
                <TaskFeedback 
                  taskId={currentTaskId}
                  templateId={selectedTemplate?.id}
                  token={token}
                  onClose={() => setShowFeedback(false)}
                />
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(resultText);
                }}
                className="bg-teal-500 hover:bg-teal-600"
              >
                Copy Code
              </Button>
              {isAppTemplate && htmlCode && (
                <Button
                  onClick={() => {
                    const blob = new Blob([htmlCode], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedTemplate?.id || 'app'}.html`;
                    a.click();
                  }}
                  className="bg-purple-500 hover:bg-purple-600"
                >
                  Download HTML
                </Button>
              )}
              <Button
                onClick={() => {
                  setSelectedTemplate(null);
                  setResult(null);
                  setFieldValues({});
                  setCurrentTaskId(null);
                  setShowFeedback(true);
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                Build Another
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show selected template form
  if (selectedTemplate) {
    const IconComponent = ICON_MAP[selectedTemplate.icon] || FileText;
    
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          <button
            onClick={() => setSelectedTemplate(null)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to templates
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
                <IconComponent className="w-7 h-7 text-teal-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{selectedTemplate.name}</h1>
                <p className="text-zinc-400">{selectedTemplate.description}</p>
              </div>
            </div>

            {/* Free tasks banner */}
            {freeTasksRemaining > 0 && (
              <div className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
                <Gift className="w-5 h-5 text-green-400" />
                <span className="text-green-300">
                  <strong>{freeTasksRemaining} free task{freeTasksRemaining !== 1 ? 's' : ''}</strong> remaining! This one's on us.
                </span>
              </div>
            )}

            {/* Video content guidelines */}
            {selectedTemplate.is_media && selectedTemplate.media_type === 'video' && (
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-purple-300 font-medium mb-2">Video Content Tips</h4>
                    <ul className="text-purple-200/80 text-sm space-y-1">
                      <li>✅ <strong>Works great:</strong> Nature, landscapes, abstract art, products, architecture</li>
                      <li>⚠️ <strong>Avoid:</strong> Real people's names, celebrities, violence, brand logos</li>
                      <li>💡 <strong>Pro tip:</strong> Your prompt will be auto-optimized for best results!</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Voiceover Options for Video */}
            {selectedTemplate.is_media && selectedTemplate.media_type === 'video' && (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-5 h-5 text-teal-400" />
                    <span className="text-white font-medium">Add AI Voiceover</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddVoiceover(!addVoiceover)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      addVoiceover ? 'bg-teal-500' : 'bg-zinc-600'
                    }`}
                    data-testid="voiceover-toggle"
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      addVoiceover ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
                
                {addVoiceover && (
                  <div className="space-y-3">
                    <p className="text-zinc-400 text-sm">
                      AI will generate a narration script and speak it over your video
                    </p>
                    <div>
                      <label className="block text-zinc-300 text-sm mb-2">Select Voice</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {voices.map((voice) => (
                          <button
                            key={voice.id}
                            type="button"
                            onClick={() => setSelectedVoice(voice.id)}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedVoice === voice.id
                                ? 'border-teal-500 bg-teal-500/10 text-teal-300'
                                : 'border-zinc-700 bg-zinc-800/50 text-zinc-300 hover:border-zinc-600'
                            }`}
                            data-testid={`voice-${voice.id}`}
                          >
                            <div className="font-medium text-sm">{voice.name}</div>
                            <div className="text-xs text-zinc-500 mt-0.5">{voice.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {selectedTemplate.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-white font-medium mb-2">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}

              {error && (
                <div className={`rounded-lg p-3 ${error.includes('Generating') ? 'bg-blue-500/10 border border-blue-500/30 text-blue-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
                  {error.includes('Generating') && <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />}
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-400 hover:to-blue-400 text-white font-semibold rounded-xl"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (user?.is_owner || user?.unlimited_usage || freeTasksRemaining > 0) ? (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Generate (Free!)
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate ({selectedTemplate?.is_media 
                      ? selectedTemplate?.media_type === 'video' ? '$5.00' : '$2.00'
                      : '$0.50'})
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show template grid
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Quick Task Templates
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            One-click AI tasks. Fill in a few fields, get instant results.
          </p>

          {/* Streak & Quick Actions */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
            {/* Streak Badge */}
            {user && streak.current_streak > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-full px-4 py-2"
              >
                <Flame className="w-5 h-5 text-orange-400" />
                <span className="text-orange-300 font-medium">
                  {streak.current_streak} day streak!
                </span>
              </motion.div>
            )}

            {/* Surprise Me Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleSurpriseMe}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full px-4 py-2 hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
              data-testid="surprise-me-btn"
            >
              <Shuffle className="w-5 h-5 text-purple-400" />
              <span className="text-purple-300 font-medium">Surprise Me!</span>
            </motion.button>

            {/* Free tasks banner */}
            {user && freeTasksRemaining > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500/20 to-teal-500/20 border border-green-500/30 rounded-full px-4 py-2"
              >
                <Gift className="w-5 h-5 text-green-400" />
                <span className="text-green-300 font-medium">
                  {freeTasksRemaining} free tasks
                </span>
              </motion.div>
            )}
          </div>

          {!user && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500/20 to-blue-500/20 border border-teal-500/30 rounded-full px-6 py-3 mt-6"
            >
              <Gift className="w-5 h-5 text-teal-400" />
              <span className="text-teal-300 font-medium">
                Sign up for 5 free tasks!
              </span>
            </motion.div>
          )}
        </motion.div>

        {/* FAVORITES SECTION */}
        {user && favorites.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-400 fill-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Your Favorites</h2>
                <p className="text-zinc-500 text-sm">Quick access to pinned templates</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {templates.filter(t => favorites.includes(t.id)).slice(0, 4).map((template) => {
                const Icon = ICON_MAP[template.icon] || Wand2;
                return (
                  <motion.button
                    key={`fav-${template.id}`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedTemplate(template)}
                    className="relative p-4 bg-zinc-800/50 border border-red-500/20 rounded-xl text-left hover:border-red-500/40 transition-all group"
                  >
                    <Icon className="w-6 h-6 text-red-400 mb-2" />
                    <h3 className="text-white font-medium text-sm">{template.name}</h3>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* RECOMMENDED FOR YOU - Personalized based on Agent Memory */}
        {user && recommendations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.02 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Recommended for You</h2>
                <p className="text-zinc-500 text-sm">Based on your Agent Memory profile</p>
              </div>
              <button
                onClick={() => navigate('/agent-memory')}
                className="ml-auto text-xs text-violet-400 hover:text-violet-300 transition-colors"
              >
                Edit Profile →
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommendations.map((template, index) => {
                const IconComponent = ICON_MAP[template.icon] || Sparkles;
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTemplateSelect(template)}
                    className="group bg-gradient-to-br from-violet-500/5 to-fuchsia-500/5 border border-violet-500/20 rounded-2xl p-5 cursor-pointer hover:border-violet-400/50 hover:bg-violet-500/10 transition-all relative overflow-hidden"
                    data-testid={`recommended-${template.id}`}
                  >
                    <div className="absolute top-2 right-2">
                      <span className="text-[10px] bg-violet-500/20 text-violet-300 px-2 py-0.5 rounded-full">
                        {template.recommendation_reason}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mb-3 mt-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <IconComponent className="w-5 h-5 text-violet-400" />
                      </div>
                    </div>
                    <h3 className="text-base font-semibold text-white mb-1">{template.name}</h3>
                    <p className="text-zinc-400 text-xs line-clamp-2">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Prompt to set up Agent Memory if no profile */}
        {user && !hasProfile && recommendations.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.02 }}
            className="mb-12"
          >
            <div 
              onClick={() => navigate('/agent-memory')}
              className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 rounded-2xl p-6 cursor-pointer hover:border-violet-400/40 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 rounded-xl flex items-center justify-center">
                  <Brain className="w-6 h-6 text-violet-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">Get Personalized Recommendations</h3>
                  <p className="text-zinc-400 text-sm">Set up your Agent Memory profile to see templates tailored to your business</p>
                </div>
                <ArrowRight className="w-5 h-5 text-violet-400" />
              </div>
            </div>
          </motion.div>
        )}

        {/* MAGIC - Custom & Unlimited Tasks - FEATURED */}
        {templates.filter(t => t.category === 'magic').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-xl flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Custom Magic</h2>
                <p className="text-zinc-500 text-sm">No limits - describe ANY task, get results</p>
              </div>
              <span className="ml-auto text-xs font-bold bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-400 px-3 py-1 rounded-full animate-pulse">UNLIMITED</span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {templates.filter(t => t.category === 'magic').map((template, index) => {
                const IconComponent = ICON_MAP[template.icon] || Wand2;
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTemplateSelect(template)}
                    className="group bg-gradient-to-br from-yellow-500/5 to-amber-500/5 border border-yellow-500/20 rounded-2xl p-6 cursor-pointer hover:border-yellow-400/50 hover:bg-yellow-500/10 transition-all"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6 text-yellow-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                    <p className="text-zinc-400 text-sm">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Media Generation Section - Images & Videos */}
        {templates.filter(t => t.category === 'media').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-xl flex items-center justify-center">
                <Image className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Create Media</h2>
                <p className="text-zinc-500 text-sm">AI-generated images, logos, and videos</p>
              </div>
              <span className="ml-auto text-xs font-bold bg-gradient-to-r from-pink-500/20 to-rose-500/20 text-pink-400 px-3 py-1 rounded-full">HOT</span>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {templates.filter(t => t.category === 'media').map((template, index) => {
                const IconComponent = ICON_MAP[template.icon] || Image;
                const isVideo = template.media_type === 'video';
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTemplateSelect(template)}
                    className="group relative bg-gradient-to-br from-pink-500/5 to-rose-500/5 border border-pink-500/20 rounded-2xl p-5 cursor-pointer hover:border-pink-400/50 hover:bg-pink-500/10 transition-all"
                    data-testid={`template-${template.id}`}
                  >
                    {/* Favorite Button */}
                    {user && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(template.id); }}
                        className="absolute top-3 right-3 p-1.5 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors"
                        data-testid={`fav-btn-${template.id}`}
                      >
                        <Heart className={`w-4 h-4 ${favorites.includes(template.id) ? 'text-red-400 fill-red-400' : 'text-zinc-500'}`} />
                      </button>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <IconComponent className="w-5 h-5 text-pink-400" />
                      </div>
                      {isVideo && (
                        <span className="text-xs bg-rose-500/20 text-rose-400 px-2 py-0.5 rounded">VIDEO</span>
                      )}
                    </div>
                    <h3 className="text-base font-semibold text-white mb-1">{template.name}</h3>
                    <p className="text-zinc-400 text-xs">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* App Building Section - Featured */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
              <Monitor className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Build Apps</h2>
              <p className="text-zinc-500 text-sm">Generate complete, working web apps instantly</p>
            </div>
            <span className="ml-auto text-xs font-bold bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full">NEW</span>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {templates.filter(t => t.category === 'apps').map((template, index) => {
              const IconComponent = ICON_MAP[template.icon] || FileText;
              return (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleTemplateSelect(template)}
                  className="group bg-gradient-to-br from-purple-500/5 to-pink-500/5 border border-purple-500/20 rounded-2xl p-6 cursor-pointer hover:border-purple-400/50 hover:bg-purple-500/10 transition-all"
                  data-testid={`template-${template.id}`}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                  <p className="text-zinc-400 text-sm">{template.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Content & Code Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Content & Code</h2>
              <p className="text-zinc-500 text-sm">Writing, emails, social posts, and code review</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.filter(t => t.category === 'content' || t.category === 'code').map((template, index) => {
              const IconComponent = ICON_MAP[template.icon] || FileText;
              return (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleTemplateSelect(template)}
                  className="group bg-zinc-900/50 border border-white/10 rounded-2xl p-6 cursor-pointer hover:border-teal-500/50 hover:bg-zinc-900/80 transition-all"
                  data-testid={`template-${template.id}`}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                  <p className="text-zinc-400 text-sm">{template.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Career & Job Seeker Section */}
        {templates.filter(t => t.category === 'career').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Career & Job Search</h2>
                <p className="text-zinc-500 text-sm">Land your dream job with AI-powered tools</p>
              </div>
              <span className="ml-auto text-xs font-bold bg-green-500/20 text-green-400 px-3 py-1 rounded-full">GET HIRED</span>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.filter(t => t.category === 'career').map((template, index) => {
                const IconComponent = ICON_MAP[template.icon] || FileText;
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTemplateSelect(template)}
                    className="group bg-zinc-900/50 border border-white/10 rounded-2xl p-6 cursor-pointer hover:border-green-500/50 hover:bg-zinc-900/80 transition-all"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6 text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                    <p className="text-zinc-400 text-sm">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Freelancer Section */}
        {templates.filter(t => t.category === 'freelance').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
                <PenLine className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Freelancer Tools</h2>
                <p className="text-zinc-500 text-sm">Win clients, manage projects & get paid</p>
              </div>
              <span className="ml-auto text-xs font-bold bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full">EARN MORE</span>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.filter(t => t.category === 'freelance').map((template, index) => {
                const IconComponent = ICON_MAP[template.icon] || FileText;
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTemplateSelect(template)}
                    className="group bg-zinc-900/50 border border-white/10 rounded-2xl p-6 cursor-pointer hover:border-cyan-500/50 hover:bg-zinc-900/80 transition-all"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6 text-cyan-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                    <p className="text-zinc-400 text-sm">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Workflows Section */}
        {templates.filter(t => t.category === 'workflows').length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Workflows</h2>
                <p className="text-zinc-500 text-sm">Multi-step AI chains that deliver complete results</p>
              </div>
              <span className="ml-auto text-xs font-bold bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full">POWER</span>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {templates.filter(t => t.category === 'workflows').map((template, index) => {
                const IconComponent = ICON_MAP[template.icon] || Zap;
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTemplateSelect(template)}
                    className="group bg-gradient-to-br from-orange-500/5 to-red-500/5 border border-orange-500/20 rounded-2xl p-6 cursor-pointer hover:border-orange-400/50 hover:bg-orange-500/10 transition-all"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-6 h-6 text-orange-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{template.name}</h3>
                    <p className="text-zinc-400 text-sm">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* CTA for non-logged in users */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12 text-center"
          >
            <Button
              onClick={() => navigate('/auth?redirect=/templates')}
              className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-400 hover:to-blue-400 px-8 py-6 text-lg"
            >
              Sign Up & Get 5 Free Tasks
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
